import React from 'react';
import './shared/flowPlannerShared.css';

const isZero = (value) => Math.abs(Number(value || 0)) < 1e-9;

/**
 * Presentational rows × intervals table with optional per-cell heatmap tint
 * and per-slot status indicator bars beneath each column header.
 *
 * Slots: { primary, secondary?, tertiary?, indicatorStatus? }
 *   indicatorStatus              → 'shortage' | 'surplus' | undefined
 *
 * Rows:  { label, dotKind?, values, emphasize?, renderValue?, cellTint?, hideZero? }
 *   dotKind                      → 'required' | 'available' | 'gap' | 'vet' | 'vto'
 *   cellTint(value, idx, values) → CSS color string (or null/undefined for none)
 *   renderValue(value, idx)      → ReactNode (overrides default text rendering)
 *   hideZero                     → render exact zeros as `zeroPlaceholder` (default true).
 *                                  Set `false` for rows where 0 is meaningful (e.g. baseline).
 */
const IntervalSummaryHeatmap = ({
  headerLabel,
  slots,
  rows,
  formatValue = (v) => Number(v ?? 0).toFixed(2),
  zeroPlaceholder = '—'
}) => (
  <table className="fp-summary-table">
    <thead>
      <tr>
        <th className="fp-summary-header-cell fp-summary-header-cell--label">{headerLabel}</th>
        {slots.map((slot, idx) => {
          const isLast = idx === slots.length - 1;
          return (
            <th key={idx} className={`fp-summary-header-cell fp-summary-header-cell--slot${isLast ? ' is-last' : ''}`}>
              <div className="fp-summary-slot-line">
                <span className="fp-summary-slot-primary">{slot.primary}</span>
                {slot.secondary && <span className="fp-summary-slot-secondary">{slot.secondary}</span>}
                {slot.tertiary && <span className="fp-summary-slot-tertiary">{slot.tertiary}</span>}
              </div>
              {slot.indicatorStatus && <div className="fp-summary-slot-indicator" data-status={slot.indicatorStatus} />}
            </th>
          );
        })}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, rIdx) => {
        const hideZero = row.hideZero !== false;
        const labelClass = `fp-summary-row-text${row.emphasize ? ' is-emphasized' : ''}`;
        const valueClass = `fp-summary-value${row.emphasize ? ' is-emphasized' : ''}`;
        return (
          <tr key={rIdx}>
            <td className="fp-summary-row-label">
              {row.dotKind && <span className="fp-summary-row-dot" data-kind={row.dotKind} />}
              <span className={labelClass}>{row.label}</span>
            </td>
            {row.values.map((value, cIdx) => {
              const tint = row.cellTint?.(value, cIdx, row.values);
              const cellStyle = tint ? { '--cell-tint': tint } : undefined;
              return (
                <td key={cIdx} className="fp-summary-value-cell" style={cellStyle}>
                  {row.renderValue ? (
                    row.renderValue(value, cIdx)
                  ) : hideZero && isZero(value) ? (
                    <span className="fp-summary-value-zero">{zeroPlaceholder}</span>
                  ) : (
                    <span className={valueClass}>{formatValue(value)}</span>
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  </table>
);

export default IntervalSummaryHeatmap;
